/* eslint-disable react/prop-types */
import { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import PushPinIcon from "@mui/icons-material/PushPin";
import CampaignIcon from "@mui/icons-material/Campaign";
import ChurchIcon from "@mui/icons-material/AccountBalance";
import GroupsIcon from "@mui/icons-material/Groups";
import AddIcon from "@mui/icons-material/Add";
import Swal from "sweetalert2";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useAuth } from "contexts/AuthContext";
import { API_BASE } from "config/api";

// ── helpers ─────────────────────────────────────────────────────────────────

function authHeaders() {
  const token = localStorage.getItem("authToken");
  return { Accept: "application/json", Authorization: `Bearer ${token}` };
}

async function apiFetch(path, options = {}) {
  const { headers: extraHeaders, params, ...rest } = options;
  const qs = params ? "?" + new URLSearchParams(params).toString() : "";
  const res = await window.fetch(`${API_BASE}/${path}${qs}`, {
    headers: { ...authHeaders(), ...(extraHeaders || {}) },
    ...rest,
  });
  return res;
}

function timeAgo(dateStr) {
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function MemberAvatar({ member, size = 36 }) {
  const photo = member?.photo;
  const name = member?.name || "?";
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return (
    <Avatar
      src={photo ? `${API_BASE}/media/${photo}` : undefined}
      sx={{ width: size, height: size, fontSize: size * 0.4, bgcolor: "primary.main" }}
    >
      {!photo && initials}
    </Avatar>
  );
}

// ── Post Announcement Dialog ─────────────────────────────────────────────────

function PostAnnouncementDialog({ open, onClose, onPosted }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [category, setCategory] = useState("");
  const [saving, setSaving] = useState(false);

  const handlePost = useCallback(async () => {
    if (!title.trim() || !body.trim()) return;
    setSaving(true);
    try {
      const res = await apiFetch("announcements", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          body: body.trim(),
          category: category.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed");
      }
      const data = await res.json();
      onPosted(data);
      setTitle("");
      setBody("");
      setCategory("");
      onClose();
    } catch (e) {
      Swal.fire("Error", e.message || "Failed to post announcement", "error");
    } finally {
      setSaving(false);
    }
  }, [title, body, category, onClose, onPosted]);

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        <CampaignIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        New Church Announcement
      </DialogTitle>
      <DialogContent>
        <TextField
          label="Title"
          fullWidth
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2, mt: 1 }}
          inputProps={{ maxLength: 255 }}
        />
        <TextField
          label="Announcement"
          fullWidth
          multiline
          rows={5}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          label="Category (optional)"
          fullWidth
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          placeholder="e.g. Events, Prayer, Notice"
          inputProps={{ maxLength: 50 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handlePost}
          disabled={saving || !title.trim() || !body.trim()}
          startIcon={saving ? <CircularProgress size={16} /> : <CampaignIcon />}
        >
          Post Announcement
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Announcement Card ────────────────────────────────────────────────────────

function AnnouncementCard({ item, canAdmin, onDelete, onPinToggle }) {
  const isPinned = item.is_pinned;
  const isChurch = item.source === "church";

  return (
    <Card
      elevation={isPinned ? 3 : 1}
      sx={{
        mb: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: isPinned ? "primary.light" : "divider",
        background: isPinned ? "linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)" : undefined,
        position: "relative",
        overflow: "visible",
      }}
    >
      {isPinned && (
        <Box
          sx={{
            position: "absolute",
            top: -10,
            left: 16,
            bgcolor: "primary.main",
            color: "white",
            borderRadius: 2,
            px: 1.5,
            py: 0.3,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            zIndex: 1,
          }}
        >
          <PushPinIcon sx={{ fontSize: 12 }} />
          <Typography variant="caption" fontWeight={700}>
            PINNED
          </Typography>
        </Box>
      )}
      <CardContent sx={{ pt: isPinned ? 3 : 2 }}>
        {/* Source chip */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
          <Chip
            icon={
              isChurch ? <ChurchIcon sx={{ fontSize: 14 }} /> : <GroupsIcon sx={{ fontSize: 14 }} />
            }
            label={item.source_label || (isChurch ? "Church" : "Group")}
            size="small"
            color={isChurch ? "primary" : "default"}
            variant={isChurch ? "filled" : "outlined"}
            sx={{ fontSize: 11 }}
          />
          {item.category && (
            <Chip label={item.category} size="small" variant="outlined" sx={{ fontSize: 11 }} />
          )}
        </Box>

        {/* Header row */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
          <MemberAvatar member={item.author} size={38} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
              {item.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {item.author?.name || "Admin"} &bull; {timeAgo(item.created_at)}
            </Typography>
          </Box>

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
            {isChurch && canAdmin && (
              <Tooltip title={isPinned ? "Unpin" : "Pin to top"}>
                <IconButton
                  size="small"
                  onClick={() => onPinToggle(item)}
                  color={isPinned ? "primary" : "default"}
                >
                  <PushPinIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {(isChurch ? canAdmin : item.can_delete) && (
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  onClick={() => onDelete(item)}
                  sx={{ color: "error.light" }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}>
          {item.body}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

const FILTER_ALL = "all";
const FILTER_CHURCH = "church";
const FILTER_GROUP = "group";

export default function Announcements() {
  const { user } = useAuth();
  const role = user?.role || "";
  const canAdmin = ["admin", "pastor", "super_admin", "branch_admin"].includes(role);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(FILTER_ALL);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadAnnouncements = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch("announcements");
      if (!res.ok) return;
      const json = await res.json();
      setItems(json.data || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  const handlePosted = useCallback((newItem) => {
    setItems((prev) => [newItem, ...prev]);
  }, []);

  const handleDelete = useCallback(async (item) => {
    const result = await Swal.fire({
      title: "Delete Announcement?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;

    const endpoint =
      item.source === "church" ? `announcements/${item.id}` : `my-group/announcements/${item.id}`;
    const res = await apiFetch(endpoint, { method: "DELETE" });
    if (res.ok) {
      setItems((prev) => prev.filter((a) => a.id !== item.id || a.source !== item.source));
    }
  }, []);

  const handlePinToggle = useCallback(async (item) => {
    const res = await apiFetch(`announcements/${item.id}/pin`, { method: "POST" });
    if (res.ok) {
      const { is_pinned } = await res.json();
      setItems((prev) =>
        prev.map((a) => (a.id === item.id && a.source === "church" ? { ...a, is_pinned } : a))
      );
    }
  }, []);

  const filtered = items.filter((item) => {
    if (filter === FILTER_CHURCH) return item.source === "church";
    if (filter === FILTER_GROUP) return item.source === "group";
    return true;
  });

  const churchCount = items.filter((i) => i.source === "church").length;
  const groupCount = items.filter((i) => i.source === "group").length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box>
            <MDTypography variant="h5" fontWeight={700}>
              Announcements
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Church-wide and group announcements in one place
            </MDTypography>
          </Box>
          {canAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
              sx={{ borderRadius: 8, textTransform: "none" }}
            >
              <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                Post Announcement
              </Box>
              <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                Post
              </Box>
            </Button>
          )}
        </Box>

        {/* Filter chips */}
        <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: "wrap" }}>
          <Chip
            label={`All (${items.length})`}
            onClick={() => setFilter(FILTER_ALL)}
            color={filter === FILTER_ALL ? "primary" : "default"}
            variant={filter === FILTER_ALL ? "filled" : "outlined"}
          />
          <Chip
            icon={<ChurchIcon sx={{ fontSize: 14 }} />}
            label={`Church (${churchCount})`}
            onClick={() => setFilter(FILTER_CHURCH)}
            color={filter === FILTER_CHURCH ? "primary" : "default"}
            variant={filter === FILTER_CHURCH ? "filled" : "outlined"}
          />
          <Chip
            icon={<GroupsIcon sx={{ fontSize: 14 }} />}
            label={`My Groups (${groupCount})`}
            onClick={() => setFilter(FILTER_GROUP)}
            color={filter === FILTER_GROUP ? "primary" : "default"}
            variant={filter === FILTER_GROUP ? "filled" : "outlined"}
          />
        </Stack>

        {/* Feed */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              textAlign: "center",
              py: 6,
              border: "1px dashed",
              borderColor: "divider",
              borderRadius: 3,
            }}
          >
            <CampaignIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
            <Typography variant="h6" color="text.secondary">
              No announcements
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filter === FILTER_ALL
                ? "There are no announcements yet."
                : `No ${filter} announcements found.`}
            </Typography>
          </Card>
        ) : (
          filtered.map((item, idx) => (
            <AnnouncementCard
              key={`${item.source}-${item.id}-${idx}`}
              item={item}
              canAdmin={canAdmin}
              onDelete={handleDelete}
              onPinToggle={handlePinToggle}
            />
          ))
        )}

        <PostAnnouncementDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onPosted={handlePosted}
        />
      </MDBox>
    </DashboardLayout>
  );
}
